// src/screens/BlogScreen.jsx

import React, { useEffect, useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  Search,
  ArrowUpRight,
  ChevronDown,
  Clock,
  BookOpen,
} from "lucide-react";

import blogData from "../data/blogData.json";

/* ======================================================
   IDENTIDAD VISUAL
====================================================== */

const DISPLAY_FONT = {
  fontFamily: '"Arial Narrow", "Roboto Condensed", Arial, sans-serif',
  fontStretch: "condensed",
};

const GRID_BACKGROUND = {
  backgroundImage: `
    linear-gradient(rgba(255,255,255,.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,.06) 1px, transparent 1px)
  `,
  backgroundSize: "64px 64px",
};

/* ======================================================
   IDIOMAS
   Compatible con MainScreen, AboutScreen y ServicesScreen.
====================================================== */


/* ======================================================
   RESPONSIVO — IDENTIDAD EDITORIAL SOLCONT

   App ya compensa el navbar. El CSS queda aislado al blog.
   Los títulos y extractos conservan su contenido completo.
   auto-fill mantiene tarjetas equilibradas incluso con
   un único resultado, sin estirarlo a todo el contenedor.
====================================================== */

const RESPONSIVE_STYLES = `
  .solcont-blog {
    --bl-section-space: clamp(2.5rem, 6vh, 5rem);
    --bl-hero-space: clamp(1.75rem, 5vh, 4rem);
    --bl-stack: clamp(1rem, 2.5vh, 1.75rem);
    --bl-gap: clamp(1.5rem, 3vw, 3rem);
    --bl-panel: clamp(1rem, 2vw, 1.5rem);
    --bl-search-overlap: clamp(1rem, 2.5vh, 1.5rem);
    padding-bottom: var(--bl-section-space);
  }
  .solcont-blog .bl-container {
    box-sizing: border-box;
    width: 100%;
    max-width: 75rem;
    margin-inline: auto;
    padding-inline: clamp(1rem, 3vw, 2.5rem);
  }
  .solcont-blog .bl-hero {
    padding-top: var(--bl-hero-space);
    padding-bottom: calc(var(--bl-hero-space) + var(--bl-search-overlap));
  }
  .solcont-blog .bl-accent { margin-top: 1rem; }
  .solcont-blog .bl-hero-title {
    margin-top: var(--bl-stack);
    font-size: clamp(2.25rem, 7vw, 3.5rem);
    line-height: 1.02;
    overflow-wrap: anywhere;
  }
  .solcont-blog .bl-tagline {
    margin-top: var(--bl-stack);
    font-size: clamp(1.125rem, 2vw, 1.375rem);
    line-height: 1.4;
  }
  .solcont-blog .bl-intro { margin-top: 0.875rem; font-size: 1rem; line-height: 1.6; }
  .solcont-blog .bl-search-wrapper { margin-top: calc(0px - var(--bl-search-overlap)); }
  .solcont-blog .bl-search-panel { padding: var(--bl-panel); }
  .solcont-blog .bl-search-form { grid-template-columns: minmax(0, 1fr); gap: 1rem; }
  .solcont-blog .bl-search-field,
  .solcont-blog .bl-sort-field {
    min-width: 0;
    min-height: 2.75rem;
    padding-block: 0.625rem;
    font-size: 1rem;
    line-height: 1.5;
  }
  .solcont-blog .bl-result-bar { margin-top: 0.875rem; gap: 0.75rem; }
  .solcont-blog .bl-result-count { max-width: 100%; overflow-wrap: anywhere; }
  .solcont-blog .bl-language-notice { margin-top: 0.875rem; padding-top: 0.875rem; line-height: 1.6; }
  .solcont-blog .bl-section { margin-top: var(--bl-gap); }
  .solcont-blog .bl-list-heading { margin-bottom: 1.25rem; gap: 0.75rem; }
  .solcont-blog .bl-list-title { min-width: 0; font-size: 1rem; line-height: 1.4; overflow-wrap: anywhere; }
  .solcont-blog .bl-article-grid {
    gap: 1.25rem;
    grid-template-columns: minmax(0, 1fr);
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 17rem), 1fr));
  }
  .solcont-blog .bl-featured-card { grid-template-columns: minmax(0, 1fr); }
  .solcont-blog .bl-card-body { padding: clamp(1.125rem, 2vw, 1.5rem); }
  .solcont-blog .bl-featured-body { padding: clamp(1.25rem, 2.5vw, 1.75rem); }
  .solcont-blog .bl-card-badges { gap: 0.5rem 0.75rem; }
  .solcont-blog .bl-language-badge { max-width: 100%; overflow-wrap: anywhere; }
  .solcont-blog .bl-card-title {
    margin-top: 1rem;
    font-size: 1.25rem;
    line-height: 1.2;
    overflow-wrap: anywhere;
  }
  .solcont-blog .bl-featured-title {
    font-size: clamp(1.375rem, 2.4vw, 2rem);
    line-height: 1.13;
  }
  .solcont-blog .bl-card-description { margin-top: 0.75rem; line-height: 1.6; overflow-wrap: anywhere; }
  .solcont-blog .bl-card-footer { padding-top: 1rem; }
  .solcont-blog .bl-read-row { margin-top: 1rem; padding-top: 1rem; }
  .solcont-blog .bl-meta { gap: 0.5rem 0.875rem; }
  .solcont-blog .bl-meta > * { min-width: 0; overflow-wrap: anywhere; }
  .solcont-blog .bl-card-media { min-width: 0; aspect-ratio: 16 / 9; }
  .solcont-blog .bl-featured-media { height: clamp(11rem, 30vw, 20rem); aspect-ratio: auto; }
  .solcont-blog .bl-empty { padding: var(--bl-section-space) var(--bl-panel); }
  .solcont-blog .bl-empty-title { margin-top: 1.25rem; font-size: clamp(1.25rem, 2.5vw, 1.5rem); overflow-wrap: anywhere; }
  .solcont-blog .bl-empty-description { line-height: 1.6; }
  .solcont-blog .bl-button { min-height: 2.75rem; max-width: 100%; white-space: normal; }

  @supports (height: 100svh) {
    .solcont-blog {
      --bl-section-space: clamp(2.5rem, 6svh, 5rem);
      --bl-hero-space: clamp(1.75rem, 5svh, 4rem);
      --bl-stack: clamp(1rem, 2.5svh, 1.75rem);
      --bl-search-overlap: clamp(1rem, 2.5svh, 1.5rem);
    }
  }
  @media (min-width: 48rem) {
    .solcont-blog .bl-search-form {
      grid-template-columns: minmax(0, 1fr) minmax(0, 14rem);
      align-items: end;
    }
  }
  @media (min-width: 60rem) {
    .solcont-blog .bl-hero-title {
      font-size: clamp(2.375rem, min(4.2vw, 7.2vh), 4.25rem);
    }
    .solcont-blog .bl-featured-card {
      grid-template-columns: minmax(0, 5fr) minmax(0, 6fr);
    }
    .solcont-blog .bl-featured-media {
      height: auto;
      min-height: clamp(12rem, 30vh, 20rem);
    }
    @supports (height: 100svh) {
      .solcont-blog .bl-hero-title {
        font-size: clamp(2.375rem, min(4.2vw, 7.2svh), 4.25rem);
      }
      .solcont-blog .bl-featured-media { min-height: clamp(12rem, 30svh, 20rem); }
    }
  }
  @media (min-width: 60rem) and (max-height: 46.25rem) {
    .solcont-blog {
      --bl-section-space: 2rem;
      --bl-hero-space: 1.5rem;
      --bl-stack: 1rem;
      --bl-gap: 1.5rem;
      --bl-panel: 1.125rem;
      --bl-search-overlap: 1rem;
    }
    .solcont-blog .bl-hero-title {
      font-size: clamp(2.25rem, min(4.2vw, 6.8vh), 3.25rem);
    }
    .solcont-blog .bl-featured-body { padding: 1.25rem; }
  }
  @media (max-width: 39.999rem) {
    .solcont-blog .bl-button { width: 100%; }
  }

  /* Sin recortes de texto, escalado global ni scroll dentro de las tarjetas. */
`;

const LANGUAGE_STORAGE_KEY = "solcont-language";
const LANGUAGE_CHANGE_EVENT = "solcont:language-change";

const LOCALES = {
  es: "es-MX",
  en: "en-US",
  fr: "fr-CA",
};

const OG_LOCALES = {
  es: "es_MX",
  en: "en_US",
  fr: "fr_CA",
};

function normalizeLanguage(value) {
  if (typeof value !== "string") return null;

  const code = value.trim().toLowerCase().split(/[-_]/)[0];

  return ["es", "en", "fr"].includes(code) ? code : null;
}

function readStoredLanguage() {
  if (typeof window === "undefined") return "es";

  try {
    return (
      normalizeLanguage(
        window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
      ) || "es"
    );
  } catch {
    return "es";
  }
}

function useLanguage(controlledLanguage) {
  const [storedLanguage, setStoredLanguage] = useState(readStoredLanguage);

  useEffect(() => {
    function handleLanguageChange(event) {
      const value =
        typeof event.detail === "string"
          ? event.detail
          : event.detail?.language;

      const next = normalizeLanguage(value);
      if (!next) return;

      setStoredLanguage(next);

      try {
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
      } catch {
        // El idioma funciona aunque el almacenamiento esté bloqueado.
      }
    }

    function handleStorage(event) {
      if (event.key === LANGUAGE_STORAGE_KEY || event.key === null) {
        setStoredLanguage(readStoredLanguage());
      }
    }

    window.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(
        LANGUAGE_CHANGE_EVENT,
        handleLanguageChange
      );
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  return normalizeLanguage(controlledLanguage) || storedLanguage;
}

/* ======================================================
   TEXTOS DE INTERFAZ
====================================================== */

const CONTENT = {
  es: {
    seoTitle: "Visión Contable | Blog de SolCont México",
    seoDescription:
      "Artículos sobre contabilidad, impuestos, negocios y estrategia fiscal para personas físicas, profesionistas y empresas en México.",
    eyebrow: "SOLCONT / BLOG",
    tagline: "Ideas claras. Mejores decisiones.",
    intro:
      "Contabilidad, impuestos y estrategia fiscal explicados con claridad. Un espacio para entender tu operación y tomar decisiones con más contexto.",
    searchLabel: "Buscar artículos",
    searchPlaceholder: "Busca un tema, título o autor…",
    sortLabel: "Ordenar por",
    newest: "Más recientes",
    oldest: "Más antiguos",
    alphabetical: "Título: A–Z",
    featured: "Artículo destacado",
    articles: "Artículos",
    moreArticles: "Más para leer",
    searchResults: "Resultados de búsqueda",
    result: "artículo",
    results: "artículos",
    matching: "para",
    read: "Leer artículo",
    readingTime: "min de lectura",
    untitled: "Artículo sin título",
    emptyTitle: "Pronto habrá nuevas ideas por aquí.",
    emptyDescription:
      "Estamos preparando contenido para ayudarte a entender mejor tu contabilidad y tus decisiones fiscales.",
    noResultsTitle: "No encontramos coincidencias.",
    noResultsDescription:
      "Prueba con otro tema, una palabra más general o el nombre del autor.",
    clearSearch: "Limpiar búsqueda",
    originalNotice:
      "Los artículos se muestran en su idioma original. El selector cambia el idioma de la interfaz.",
    languagePrefix: "Contenido en",
    originalLanguage: "Idioma original",
    languageNames: {
      es: "español",
      en: "inglés",
      fr: "francés",
    },
  },

  en: {
    seoTitle: "Visión Contable | SolCont México Blog",
    seoDescription:
      "Articles about accounting, taxes, business and tax strategy for individuals, professionals and companies in Mexico.",
    eyebrow: "SOLCONT / BLOG",
    tagline: "Clear insights. Better decisions.",
    intro:
      "Accounting, taxes and tax strategy explained clearly. A space to understand your operations and make better-informed decisions.",
    searchLabel: "Search articles",
    searchPlaceholder: "Search by topic, title or author…",
    sortLabel: "Sort by",
    newest: "Newest first",
    oldest: "Oldest first",
    alphabetical: "Title: A–Z",
    featured: "Featured article",
    articles: "Articles",
    moreArticles: "More to read",
    searchResults: "Search results",
    result: "article",
    results: "articles",
    matching: "for",
    read: "Read article",
    readingTime: "min read",
    untitled: "Untitled article",
    emptyTitle: "New ideas are coming soon.",
    emptyDescription:
      "We are preparing content to help you better understand your accounting and tax decisions.",
    noResultsTitle: "No matching articles.",
    noResultsDescription:
      "Try another topic, a broader keyword or the author’s name.",
    clearSearch: "Clear search",
    originalNotice:
      "Articles are shown in their original language. The language selector changes the interface.",
    languagePrefix: "Content in",
    originalLanguage: "Original language",
    languageNames: {
      es: "Spanish",
      en: "English",
      fr: "French",
    },
  },

  fr: {
    seoTitle: "Visión Contable | Blog de SolCont México",
    seoDescription:
      "Des articles sur la comptabilité, les impôts, les entreprises et la stratégie fiscale pour les particuliers et les professionnels au Mexique.",
    eyebrow: "SOLCONT / BLOG",
    tagline: "Des idées claires. Des décisions éclairées.",
    intro:
      "La comptabilité, les impôts et la stratégie fiscale expliqués clairement. Un espace pour comprendre votre activité et prendre des décisions mieux informées.",
    searchLabel: "Rechercher des articles",
    searchPlaceholder: "Rechercher un sujet, un titre ou un auteur…",
    sortLabel: "Trier par",
    newest: "Les plus récents",
    oldest: "Les plus anciens",
    alphabetical: "Titre : A–Z",
    featured: "Article à la une",
    articles: "Articles",
    moreArticles: "À lire également",
    searchResults: "Résultats de recherche",
    result: "article",
    results: "articles",
    matching: "pour",
    read: "Lire l’article",
    readingTime: "min de lecture",
    untitled: "Article sans titre",
    emptyTitle: "De nouvelles idées arrivent bientôt.",
    emptyDescription:
      "Nous préparons du contenu pour vous aider à mieux comprendre votre comptabilité et vos décisions fiscales.",
    noResultsTitle: "Aucun article correspondant.",
    noResultsDescription:
      "Essayez un autre sujet, un mot-clé plus général ou le nom de l’auteur.",
    clearSearch: "Effacer la recherche",
    originalNotice:
      "Les articles sont présentés dans leur langue d’origine. Le sélecteur de langue modifie l’interface.",
    languagePrefix: "Contenu en",
    originalLanguage: "Langue d’origine",
    languageNames: {
      es: "espagnol",
      en: "anglais",
      fr: "français",
    },
  },
};

/* ======================================================
   HELPERS DE CONTENIDO
====================================================== */

function plainText(value) {
  if (typeof value !== "string" && typeof value !== "number") {
    return "";
  }

  return String(value)
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;|&#160;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeSearch(value) {
  return plainText(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function getBodyText(post) {
  // Compatible con body1...body15 y con más bloques futuros.
  return Object.keys(post)
    .filter((key) => /^body\d+$/.test(key))
    .sort((a, b) => Number(a.slice(4)) - Number(b.slice(4)))
    .map((key) => plainText(post[key]))
    .filter(Boolean)
    .join(" ");
}

function estimateReadingTime(text) {
  const words = text.trim().split(/\s+/).filter(Boolean).length;

  // No mostramos una duración inventada si no hay cuerpo.
  return words > 0 ? Math.max(1, Math.ceil(words / 200)) : null;
}

function getContentLanguage(post) {
  const raw =
    typeof post.language === "string"
      ? post.language
      : typeof post.lang === "string"
      ? post.lang
      : "es";

  const code = raw.trim().replace(/_/g, "-").toLowerCase();

  return /^[a-z]{2,3}(?:-[a-z0-9]{2,8})*$/.test(code) ? code : "es";
}

/*
 * Fechas:
 * - YYYY-MM-DD
 * - DD/MM/YYYY o DD-MM-YYYY
 * - Timestamps ISO con zona horaria
 *
 * Las fechas sin hora se interpretan en UTC para evitar
 * que se muestre el día anterior en México.
 */
function parsePostDate(value) {
  if (typeof value !== "string") return null;

  const raw = value.trim();
  if (!raw) return null;

  const isoDate = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const localDate = raw.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);

  if (isoDate || localDate) {
    const year = Number(isoDate ? isoDate[1] : localDate[3]);
    const month = Number(isoDate ? isoDate[2] : localDate[2]);
    const day = Number(isoDate ? isoDate[3] : localDate[1]);

    const date = new Date(Date.UTC(year, month - 1, day));

    if (
      date.getUTCFullYear() !== year ||
      date.getUTCMonth() !== month - 1 ||
      date.getUTCDate() !== day
    ) {
      return null;
    }

    return date;
  }

  if (
    /^\d{4}-\d{2}-\d{2}T/.test(raw) &&
    /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw)
  ) {
    const timestamp = Date.parse(raw);

    if (Number.isFinite(timestamp)) return new Date(timestamp);
  }

  return null;
}

function preparePosts(data) {
  if (!Array.isArray(data)) return [];

  return data
    .filter(
      (post) =>
        post &&
        typeof post === "object" &&
        (typeof post.id === "string" || typeof post.id === "number") &&
        String(post.id).trim() !== ""
    )
    .map((post, index) => {
      const body = getBodyText(post);
      const date = parsePostDate(post.date);
      const numericId = Number(post.id);

      return {
        id: post.id,
        key: `${post.id}-${index}`,
        index,
        title: plainText(post.title),
        description: plainText(post.description),
        author: plainText(post.autor),
        rawDate: plainText(post.date),
        date,
        timestamp: date ? date.getTime() : null,
        numericId: Number.isFinite(numericId) ? numericId : null,
        image: typeof post.image === "string" ? post.image.trim() : "",
        contentLanguage: getContentLanguage(post),
        readingMinutes: estimateReadingTime(body),
        searchText: normalizeSearch(
          [
            post.title,
            post.description,
            post.autor,
            post.date,
            body,
          ]
            .map(plainText)
            .join(" ")
        ),
      };
    });
}

function compareChronologically(a, b, oldestFirst = false) {
  const direction = oldestFirst ? 1 : -1;

  if (a.timestamp !== null && b.timestamp !== null) {
    const difference = a.timestamp - b.timestamp;

    if (difference !== 0) return difference * direction;
  } else if (a.timestamp !== null || b.timestamp !== null) {
    // Fechas desconocidas al final.
    return a.timestamp !== null ? -1 : 1;
  }

  // Respaldo compatible con la numeración del archivo original.
  if (a.numericId !== null && b.numericId !== null) {
    const difference = a.numericId - b.numericId;

    if (difference !== 0) return difference * direction;
  }

  return a.index - b.index;
}

/* ======================================================
   PANTALLA
====================================================== */

export default function BlogScreen({
  language: controlledLanguage,
} = {}) {
  const language = useLanguage(controlledLanguage);
  const t = CONTENT[language];

  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");

  const screenId = useId();
  const searchId = `${screenId}-search`;
  const sortId = `${screenId}-sort`;
  const resultCountId = `${screenId}-count`;
  const articlesTitleId = `${screenId}-articles`;

  const base = useMemo(() => preparePosts(blogData), []);

  const collator = useMemo(
    () => new Intl.Collator(LOCALES[language], { sensitivity: "base" }),
    [language]
  );

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(LOCALES[language], {
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: "UTC",
      }),
    [language]
  );

  const normalizedQuery = normalizeSearch(query);
  const searching = Boolean(query.trim());

  const sorted = useMemo(() => {
    const matches = normalizedQuery
      ? base.filter((post) => post.searchText.includes(normalizedQuery))
      : base;

    return [...matches].sort((a, b) => {
      if (sortBy === "title") {
        return (
          collator.compare(a.title || t.untitled, b.title || t.untitled) ||
          a.index - b.index
        );
      }

      return compareChronologically(a, b, sortBy === "oldest");
    });
  }, [base, normalizedQuery, sortBy, collator, t.untitled]);

  const featured = !searching ? sorted[0] : null;
  const gridPosts = featured ? sorted.slice(1) : sorted;

  const hasOtherLanguages = base.some(
    (post) => post.contentLanguage.split("-")[0] !== language
  );

  const canonical =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}`
      : "https://solcont.com.mx/blog";

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Visión Contable",
    description: t.seoDescription,
    url: canonical,
    publisher: {
      "@type": "Organization",
      name: "SolCont México",
      url: "https://solcont.com.mx",
    },
    inLanguage: base.length
      ? [...new Set(base.map((post) => post.contentLanguage))]
      : "es",
  };

  const resultText = `${sorted.length} ${
    sorted.length === 1 ? t.result : t.results
  }`;

  return (
    <div lang={language} className="solcont-blog bg-[#F7F3E9] text-[#081B3E]">
      <style>{RESPONSIVE_STYLES}</style>
      <Helmet>
        <title>{t.seoTitle}</title>
        <meta name="description" content={t.seoDescription} />
        <link rel="canonical" href={canonical} />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={t.seoTitle} />
        <meta property="og:description" content={t.seoDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content="SolCont México" />
        <meta property="og:locale" content={OG_LOCALES[language]} />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={t.seoTitle} />
        <meta name="twitter:description" content={t.seoDescription} />

        <script type="application/ld+json">
          {JSON.stringify(structuredData).replace(/</g, "\\u003c")}
        </script>
      </Helmet>

      {/* ==============================================
          HERO
      ============================================== */}

      <section className="relative isolate overflow-hidden bg-[#1648EE] text-[#F7F3E9]">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={GRID_BACKGROUND}
        />

        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-52 -top-36 h-[600px] w-[600px]"
        >
          <div className="absolute inset-0 rounded-full border border-white/20" />
          <div className="absolute inset-24 rounded-full border border-white/20" />

          <span
            className="absolute left-[80px] top-[86px] h-5 w-5 rounded-full bg-[#F72585]"
          />
        </div>

        <div className="bl-container bl-hero relative">
          <p className="text-xs font-semibold tracking-[0.18em] text-white/80">
            {t.eyebrow}
          </p>

          <span
            aria-hidden="true"
            className="bl-accent block h-1 w-20 rounded-full bg-[#F72585]"
          />

          <h1
            lang="es"
            className="bl-hero-title max-w-4xl font-black uppercase tracking-tight"
            style={DISPLAY_FONT}
          >
            Visión
            <br />
            Contable.
          </h1>

          <p className="bl-tagline font-bold">
            {t.tagline}
          </p>

          <p className="bl-intro max-w-2xl text-white/85">
            {t.intro}
          </p>
        </div>
      </section>

      {/* ==============================================
          BÚSQUEDA Y ORDEN
      ============================================== */}

      <div className="bl-container bl-search-wrapper relative z-10">
        <div className="bl-search-panel rounded-2xl border border-[#081B3E]/10 bg-[#F7F3E9] shadow-sm">
          <form
            role="search"
            aria-label={t.searchLabel}
            onSubmit={(event) => event.preventDefault()}
            className="bl-search-form grid"
          >
            <div className="min-w-0">
              <label
                htmlFor={searchId}
                className="mb-2 block text-xs font-bold uppercase tracking-wider text-[#081B3E]/75"
              >
                {t.searchLabel}
              </label>

              <div className="relative">
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#1648EE]"
                />

                <input
                  id={searchId}
                  type="search"
                  name="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t.searchPlaceholder}
                  aria-describedby={resultCountId}
                  autoComplete="off"
                  className="bl-search-field w-full rounded-xl border border-[#081B3E]/20 bg-white pl-12 pr-4 text-[#081B3E] placeholder:text-slate-500 outline-none transition-colors focus:border-[#1648EE] focus:ring-2 focus:ring-[#1648EE]/15"
                />
              </div>
            </div>

            <div className="min-w-0">
              <label
                htmlFor={sortId}
                className="mb-2 block text-xs font-bold uppercase tracking-wider text-[#081B3E]/75"
              >
                {t.sortLabel}
              </label>

              <div className="relative">
                <select
                  id={sortId}
                  value={sortBy}
                  onChange={(event) => setSortBy(event.target.value)}
                  className="bl-sort-field w-full appearance-none rounded-xl border border-[#081B3E]/20 bg-white pl-4 pr-11 outline-none transition-colors focus:border-[#1648EE] focus:ring-2 focus:ring-[#1648EE]/15"
                >
                  <option value="newest">{t.newest}</option>
                  <option value="oldest">{t.oldest}</option>
                  <option value="title">{t.alphabetical}</option>
                </select>

                <ChevronDown
                  aria-hidden="true"
                  className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#1648EE]"
                />
              </div>
            </div>
          </form>

          <div className="bl-result-bar flex flex-wrap items-center justify-between">
            <p
              id={resultCountId}
              role="status"
              aria-atomic="true"
              className="bl-result-count min-w-0 break-words text-sm text-[#081B3E]/70"
            >
              <span className="font-bold text-[#081B3E]">
                {resultText}
              </span>

              {searching && (
                <>
                  {" "}
                  {t.matching}{" "}
                  <span className="font-medium">“{query.trim()}”</span>
                </>
              )}
            </p>

            {searching && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="min-h-11 rounded-lg px-3 text-sm font-bold text-[#1648EE] underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1648EE]"
              >
                {t.clearSearch}
              </button>
            )}
          </div>

          {hasOtherLanguages && (
            <p className="bl-language-notice border-t border-[#081B3E]/10 text-xs text-[#081B3E]/70">
              {t.originalNotice}
            </p>
          )}
        </div>
      </div>

      {/* ==============================================
          DESTACADO
      ============================================== */}

      {featured && (
        <section
          aria-label={t.featured}
          className="bl-container bl-section"
        >
          <ArticleCard
            post={featured}
            t={t}
            dateFormatter={dateFormatter}
            featured
          />
        </section>
      )}

      {/* ==============================================
          LISTADO
          No muestra estado vacío si ya existe destacado.
      ============================================== */}

      {gridPosts.length > 0 && (
        <section
          aria-labelledby={articlesTitleId}
          className="bl-container bl-section"
        >
          <div className="bl-list-heading flex items-center">
            <span
              aria-hidden="true"
              className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#F72585]"
            />

            <h2
              id={articlesTitleId}
              className="bl-list-title font-bold uppercase tracking-wide"
            >
              {searching
                ? t.searchResults
                : featured
                ? t.moreArticles
                : t.articles}
            </h2>

            <span
              aria-hidden="true"
              className="h-px flex-1 bg-[#081B3E]/15"
            />
          </div>

          <ul className="bl-article-grid grid">
            {gridPosts.map((post) => (
              <li key={post.key} className="min-w-0">
                <ArticleCard
                  post={post}
                  t={t}
                  dateFormatter={dateFormatter}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ==============================================
          ESTADO VACÍO REAL
      ============================================== */}

      {sorted.length === 0 && (
        <section
          className="bl-container bl-section"
          aria-label={searching ? t.searchResults : t.articles}
        >
          <div className="bl-empty rounded-3xl border border-[#081B3E]/15 bg-white text-center">
            <span
              aria-hidden="true"
              className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#1648EE]/10 text-[#1648EE]"
            >
              {searching ? (
                <Search className="h-7 w-7" />
              ) : (
                <BookOpen className="h-7 w-7" />
              )}
            </span>

            <h2 className="bl-empty-title font-bold">
              {searching ? t.noResultsTitle : t.emptyTitle}
            </h2>

            <p className="bl-empty-description mx-auto mt-4 max-w-md text-sm text-[#081B3E]/75">
              {searching ? t.noResultsDescription : t.emptyDescription}
            </p>

            {searching && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="bl-button mt-5 inline-flex items-center justify-center rounded-xl bg-[#1648EE] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#103AC7] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#1648EE]"
              >
                {t.clearSearch}
              </button>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

/* ======================================================
   TARJETA EDITORIAL
   Mantiene las rutas originales /article/:id.
====================================================== */

function ArticleCard({ post, t, dateFormatter, featured = false }) {
  const titleId = useId();
  const title = post.title || t.untitled;

  const contentLanguageBase = post.contentLanguage.split("-")[0];
  const languageName = t.languageNames[contentLanguageBase];

  const Heading = featured ? "h2" : "h3";

  return (
    <article className="h-full">
      <Link
        to={`/article/${post.id}`}
        aria-labelledby={titleId}
        className={`group h-full overflow-hidden rounded-2xl
                    border border-[#081B3E]/15 bg-white
                    transition-colors hover:border-[#1648EE]/60
                    focus-visible:outline focus-visible:outline-2
                    focus-visible:outline-offset-4
                    focus-visible:outline-[#1648EE] ${
          featured
            ? "bl-featured-card grid"
            : "flex flex-col"
        }`}
      >
        <ArticleImage src={post.image} featured={featured} />

        <div
          className={`flex min-w-0 flex-1 flex-col ${
            featured ? "bl-featured-body" : "bl-card-body"
          }`}
        >
          <div className="bl-card-badges flex flex-wrap items-center">
            {featured && (
              <span
                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#1648EE]"
              >
                <span
                  aria-hidden="true"
                  className="h-2 w-2 rounded-full bg-[#F72585]"
                />
                {t.featured}
              </span>
            )}

            <span
              className="bl-language-badge rounded-full border border-[#081B3E]/10 bg-[#F7F3E9] px-3 py-1 text-xs text-[#081B3E]/75"
            >
              {languageName
                ? `${t.languagePrefix} ${languageName}`
                : `${t.originalLanguage}: ${post.contentLanguage}`}
            </span>
          </div>

          <Heading
            id={titleId}
            lang={post.title ? post.contentLanguage : undefined}
            className={`bl-card-title font-bold tracking-tight
                        transition-colors group-hover:text-[#1648EE] ${
              featured
                ? "bl-featured-title"
                : ""
            }`}
            style={DISPLAY_FONT}
          >
            {title}
          </Heading>

          {post.description && (
            <p
              lang={post.contentLanguage}
              className="bl-card-description text-sm text-[#081B3E]/75"
            >
              {post.description}
            </p>
          )}

          <div className="bl-card-footer mt-auto">
            <ArticleMeta
              post={post}
              t={t}
              dateFormatter={dateFormatter}
            />

            <div
              className="bl-read-row flex items-center justify-between gap-4 border-t border-[#081B3E]/10"
            >
              <span className="text-sm font-bold text-[#1648EE]">
                {t.read}
              </span>

              <span
                aria-hidden="true"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#1648EE] text-white transition-colors group-hover:bg-[#081B3E]"
              >
                <ArrowUpRight className="h-5 w-5" />
              </span>
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
}

/* ======================================================
   IMAGEN CON RESPALDO GRÁFICO
====================================================== */

function ArticleImage({ src, featured }) {
  const [failedSource, setFailedSource] = useState(null);
  const showImage = Boolean(src) && failedSource !== src;

  return (
    <div
      className={`bl-card-media relative overflow-hidden bg-[#1648EE] ${
        featured
          ? "bl-featured-media"
          : ""
      }`}
    >
      {showImage ? (
        <img
          src={src}
          alt=""
          loading={featured ? "eager" : "lazy"}
          decoding="async"
          onError={() => setFailedSource(src)}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <div
          aria-hidden="true"
          className="absolute inset-0 overflow-hidden"
          style={GRID_BACKGROUND}
        >
          <div
            className="absolute -right-20 -top-16 h-80 w-80 rounded-full border border-white/35"
          />

          <div
            className="absolute -right-4 top-0 h-48 w-48 rounded-full border border-white/35"
          />

          <span
            className="absolute right-40 top-12 h-5 w-5 rounded-full bg-[#F72585]"
          />

          <span
            className="absolute bottom-8 left-8 h-1 w-20 rounded-full bg-[#F72585]"
          />
        </div>
      )}
    </div>
  );
}

/* ======================================================
   FECHA, AUTOR Y LECTURA
====================================================== */

function ArticleMeta({ post, t, dateFormatter }) {
  return (
    <div className="bl-meta flex flex-wrap items-center text-xs leading-5 text-[#081B3E]/70">
      {post.date ? (
        <time dateTime={post.date.toISOString()}>
          {dateFormatter.format(post.date)}
        </time>
      ) : post.rawDate ? (
        <span lang={post.contentLanguage}>{post.rawDate}</span>
      ) : null}

      {post.readingMinutes !== null && (
        <span className="inline-flex items-center gap-1.5">
          <Clock aria-hidden="true" className="h-3.5 w-3.5" />
          {post.readingMinutes} {t.readingTime}
        </span>
      )}

      {post.author && (
        <span lang={post.contentLanguage} className="font-medium">
          {post.author}
        </span>
      )}
    </div>
  );
}
